/**
 * Reliability State
 *
 * Pure reducer for the site reliability view. A failed refresh keeps the last good
 * data and records the error, so the UI can show stale data with a warning.
 */
import type { ReliabilityResult } from '../api/reliabilityApiClient';
import type { SiteReliability } from '../models/reliabilityModels';

export interface ReliabilityState {
  readonly data: SiteReliability | null;
  readonly isLoading: boolean;
  readonly isNotFound: boolean;
  /** Most recent failure. Data, if any, is from the last successful request. */
  readonly error: string | null;
}

export type ReliabilityAction =
  | { readonly type: 'reset' }
  | { readonly type: 'idle' }
  | { readonly type: 'loaded'; readonly data: SiteReliability }
  | { readonly type: 'not_found' }
  | { readonly type: 'failed'; readonly message: string };

export const initialReliabilityState: ReliabilityState = {
  data: null,
  isLoading: true,
  isNotFound: false,
  error: null,
};

export function reliabilityReducer(
  state: ReliabilityState,
  action: ReliabilityAction
): ReliabilityState {
  switch (action.type) {
    case 'reset':
      return initialReliabilityState;
    case 'idle':
      return { data: null, isLoading: false, isNotFound: false, error: null };
    case 'loaded':
      return { data: action.data, isLoading: false, isNotFound: false, error: null };
    case 'not_found':
      return { data: null, isLoading: false, isNotFound: true, error: null };
    case 'failed':
      return { ...state, isLoading: false, error: action.message };
    default: {
      const unhandled: never = action;
      return unhandled;
    }
  }
}

export function resultToAction(result: ReliabilityResult): ReliabilityAction {
  switch (result.status) {
    case 'ok':
      return { type: 'loaded', data: result.data };
    case 'not_found':
      return { type: 'not_found' };
    case 'error':
      return { type: 'failed', message: result.message };
    default: {
      const unhandled: never = result;
      return unhandled;
    }
  }
}
